/**
 * @gohighlevel/chat-widget-core — headless chat client.
 *
 * Owns transport, security and conversation state for the HighLevel chat
 * widget backend. Renders nothing: a host subscribes to state and issues
 * commands, and draws whatever UI it likes.
 *
 * Two hosts are planned:
 *   - the Stencil widget in this repo (verification that the core is correct)
 *   - UIs generated by AI Studio into customer sites
 *
 * ## Why this exists
 *
 * The security layer must not be reimplemented per host. It is:
 *   - a single-use `requestToken` fetched from /chat-widget/public/open,
 *     decrypted client-side, burned after use, refreshed on rejection
 *   - a minimum submit interval the server enforces as "submitted too quickly"
 *   - Turnstile / reCAPTCHA producing a `captchaToken`
 *   - a bearer token and conversation identity established by the first send
 *
 * Reimplementing that in generated code would put the decryption key in
 * customer source and leave every site to be fixed separately when the
 * backend contract moves. It lives here instead, versioned, in one place.
 *
 * ## Rules for hosts
 *
 * - Never call the chat endpoints directly. Everything goes through the client.
 * - Never author consent, legal or A2P copy. Render `state.config.compliance`
 *   verbatim; it is the sub-account's, and it is regulated.
 * - Never persist tokens. The client owns storage.
 * - Never send while `state.canSend` is false — disable the control instead.
 */

export * from './types';

export { createChatClient } from './client';

/**
 * Host seams. HOSTING.md tells integrators to call these, so they belong on
 * the entry: the bundle has one entry point, and a subpath import such as
 * `@gohighlevel/chat-widget-core/i18n` does not resolve against it.
 *
 * `setTranslator` routes every visitor-facing string the core produces through
 * the host's own catalogue — without it the core's English defaults are the
 * only thing a non-English site can show. `setHostUpdater` lets a rendering
 * host be told when to re-read state; the Stencil widget installs Stencil's
 * `forceUpdate` through it.
 */
export { setTranslator, type Translate } from './internal/host/i18n';
export { setHostUpdater, type HostUpdater } from './internal/host/host-bridge';


/**
 * Semver of the core, stamped into requests and exposed on the page so
 * support can identify which core a given site is running. A generated site
 * pins a version at build time and may never be rebuilt, so changes here are
 * additive only.
 */
export const CORE_VERSION = '0.1.0';

/**
 * `createChatClient` is re-exported above from ./client.
 *
 * ```ts
 * const chat = createChatClient({
 *   locationId, widgetId, chatType: 'liveChat',
 *   marketplaceUrl, source: 'ai-studio-native',
 * });
 *
 * const stop = chat.subscribe(render);
 * await chat.open();
 * ```
 *
 * Opening, state, sending, receiving, contact-form submission, attachments and
 * feedback are implemented. Voice is not, and rejects with a clear message, so
 * a host integrating against it finds out immediately rather than watching
 * nothing happen.
 *
 * Support tooling lives in the separate `core-debug` bundle, not here: its
 * request-token probe mints and stores a real token, which is not something a
 * visitor's page should be able to do.
 */
